import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Bell, Crown, TrendingUp, Award, Activity, CheckCheck, ArrowLeft,
    Wifi, WifiOff, Search, Filter, ExternalLink, Sparkles, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
    level_up: { icon: Crown, label: 'Tier Promotions', color: 'yellow' },
    ranking_change: { icon: TrendingUp, label: 'Ranking Movements', color: 'cyan' },
    verification: { icon: Award, label: 'Verifications', color: 'green' },
    milestone: { icon: Sparkles, label: 'Milestones', color: 'purple' },
    system: { icon: Activity, label: 'System', color: 'blue' },
};

const SEVERITY_BADGE = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/40',
    success: 'bg-green-500/20 text-green-400 border-green-500/40',
    warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
};

const NotificationsPage = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ category: 'all', read: 'all', search: '' });
    const [unreadCount, setUnreadCount] = useState(0);
    const [wsStatus, setWsStatus] = useState('disconnected');
    const wsRef = useRef(null);

    useEffect(() => {
        loadNotifications();
        loadUnreadCount();
        connectWebSocket();
        return () => {
            wsRef.current?.close();
        };
        // eslint-disable-next-line
    }, []);

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/api/notifications?limit=100`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(res.data.notifications || []);
        } catch (e) {
            toast.error('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const loadUnreadCount = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/api/notifications/unread-count`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUnreadCount(res.data.unread_count || 0);
        } catch (e) {}
    };

    const connectWebSocket = () => {
        // Subscribe to federation alerts for live updates
        const wsUrl = API_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
        const ws = new WebSocket(`${wsUrl}/api/federation-alerts/ws`);
        wsRef.current = ws;
        ws.onopen = () => setWsStatus('connected');
        ws.onclose = () => setWsStatus('disconnected');
        ws.onmessage = (msg) => {
            try {
                const data = JSON.parse(msg.data);
                if (data.type === 'new_alert') {
                    // Fanout creates notification doc; reload list
                    loadNotifications();
                    loadUnreadCount();
                    toast(data.data.title, { description: data.data.message });
                }
            } catch (e) {}
        };
    };

    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            setUnreadCount(c => Math.max(0, c - 1));
        } catch (e) {}
    };

    const markAllRead = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/notifications/mark-all-read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
            toast.success('All marked as read');
        } catch (e) {
            toast.error('Failed');
        }
    };

    const handleClick = (n) => {
        if (!n.read) markAsRead(n.id);
        if (n.link) window.location.href = n.link;
    };

    // Filter
    const filtered = notifications.filter(n => {
        if (filter.category !== 'all' && (n.category || 'system') !== filter.category) return false;
        if (filter.read === 'unread' && n.read) return false;
        if (filter.read === 'read' && !n.read) return false;
        if (filter.search) {
            const q = filter.search.toLowerCase();
            return (n.title || '').toLowerCase().includes(q) || (n.message || '').toLowerCase().includes(q);
        }
        return true;
    });

    // Group by category counts
    const counts = {};
    notifications.forEach(n => {
        const c = n.category || 'system';
        counts[c] = (counts[c] || 0) + 1;
    });

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            <div className="border-b border-white/10 bg-[#0d2847] sticky top-0 z-10 backdrop-blur-md">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Link to="/dashboard"><Button variant="ghost" size="sm" data-testid="notif-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button></Link>
                        <div>
                            <h1 className="text-xl font-bold flex items-center gap-2">
                                <Bell className="w-5 h-5 text-yellow-400" />
                                Notifications
                                {unreadCount > 0 && (
                                    <span className="text-xs px-2 py-0.5 bg-red-500 text-white rounded-full" data-testid="notif-unread-count">
                                        {unreadCount} new
                                    </span>
                                )}
                            </h1>
                            <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                                {wsStatus === 'connected'
                                    ? <span className="text-green-400 flex items-center gap-1"><Wifi className="w-3 h-3" />Live updates on</span>
                                    : <span className="text-muted-foreground flex items-center gap-1"><WifiOff className="w-3 h-3" />Offline</span>
                                }
                                · {notifications.length} total
                            </p>
                        </div>
                    </div>
                    <Button onClick={markAllRead} variant="outline" size="sm" className="border-white/10" disabled={unreadCount === 0} data-testid="notif-mark-all-read-btn">
                        <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
                    </Button>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar: Category quick filters */}
                <div className="space-y-2">
                    <CategoryButton
                        label="All Categories"
                        count={notifications.length}
                        active={filter.category === 'all'}
                        onClick={() => setFilter({ ...filter, category: 'all' })}
                        testId="cat-all"
                    />
                    {Object.entries(CATEGORY_META).map(([key, meta]) => (
                        <CategoryButton
                            key={key}
                            icon={meta.icon}
                            label={meta.label}
                            color={meta.color}
                            count={counts[key] || 0}
                            active={filter.category === key}
                            onClick={() => setFilter({ ...filter, category: key })}
                            testId={`cat-${key}`}
                        />
                    ))}
                </div>

                {/* Main list */}
                <div className="lg:col-span-3 space-y-4">
                    {/* Filter bar */}
                    <div className="flex flex-wrap gap-3 items-center bg-[#0d2847] border border-white/10 rounded-lg p-3">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search notifications..."
                                value={filter.search}
                                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                                className="pl-9 bg-[#0a1628] border-white/10 h-9"
                                data-testid="notif-search-input"
                            />
                        </div>
                        <Select value={filter.read} onValueChange={(v) => setFilter({ ...filter, read: v })}>
                            <SelectTrigger className="w-[150px] bg-[#0a1628] border-white/10 h-9" data-testid="notif-read-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="unread">Unread only</SelectItem>
                                <SelectItem value="read">Read only</SelectItem>
                            </SelectContent>
                        </Select>
                        <span className="text-xs text-muted-foreground">{filtered.length} shown</span>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                    ) : filtered.length === 0 ? (
                        <Card className="bg-[#0d2847] border-white/10 p-12 text-center">
                            <Bell className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                            <p className="text-sm text-muted-foreground">No notifications match the current filter</p>
                        </Card>
                    ) : (
                        <div className="space-y-2" data-testid="notif-list">
                            {filtered.map(n => <NotificationItem key={n.id} notification={n} onClick={() => handleClick(n)} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const CategoryButton = ({ icon: Icon, label, count, active, color = 'gray', onClick, testId }) => (
    <button
        onClick={onClick}
        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${active ? `bg-${color}-500/20 border-${color}-500/40` : 'bg-[#0d2847] border-white/10 hover:border-white/30'}`}
        data-testid={testId}
    >
        <div className="flex items-center gap-2">
            {Icon && <Icon className={`w-4 h-4 text-${color}-400`} />}
            <span className="text-sm">{label}</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${count > 0 ? 'bg-white/10' : 'bg-white/5 text-muted-foreground'}`}>{count}</span>
    </button>
);

const NotificationItem = ({ notification, onClick }) => {
    const cat = notification.category || 'system';
    const meta = CATEGORY_META[cat] || CATEGORY_META.system;
    const Icon = meta.icon;
    const unread = !notification.read;
    const sevClass = SEVERITY_BADGE[notification.severity] || SEVERITY_BADGE.info;

    const ago = formatTimeAgo(notification.created_at);

    return (
        <Card
            onClick={onClick}
            className={`cursor-pointer p-4 transition-all border ${unread ? `bg-${meta.color}-500/5 border-${meta.color}-500/30 hover:border-${meta.color}-500/60` : 'bg-[#0d2847] border-white/10 hover:border-white/30'}`}
            data-testid={`notif-item-${notification.id}`}
        >
            <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg bg-${meta.color}-500/20 flex-shrink-0`}>
                    <Icon className={`w-4 h-4 text-${meta.color}-400`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <h3 className={`text-sm font-semibold ${unread ? 'text-white' : 'text-muted-foreground'}`}>{notification.title}</h3>
                            {notification.severity && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase ${sevClass}`}>{notification.severity}</span>
                            )}
                            {unread && <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />}
                        </div>
                        <span className="text-xs text-muted-foreground flex-shrink-0">{ago}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{notification.message}</p>
                    {notification.link && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300">
                            <ExternalLink className="w-3 h-3" /> Open
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
};

export default NotificationsPage;
